import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { User, fetchUser } from "./userApi";
import styles from "./UserShared.module.css";
import {
  ArrowLeft,
  Edit,
  Music,
  Plus,
  User as UserIcon,
  Mail,
  Calendar,
  Activity,
  Smartphone,
  Settings,
  Copy,
  Check,
  Globe,
  Trophy,
} from "lucide-react";
import AddDeviceModal from "./AddDeviceModal";
import AssignSongModal from "./AssignSongModal";
import { Button, Card, Input, TextArea, Badge } from "../../components/common";
import { sendSingleNotification } from "../notifications/sendSingleNotificationApi";

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Modal states
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [showSongModal, setShowSongModal] = useState(false);
  const [showNotifModal, setShowNotifModal] = useState(false);

  // Bildirim modalı state
  const [notifTitle, setNotifTitle] = useState("");
  const [notifBody, setNotifBody] = useState("");
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifResult, setNotifResult] = useState<string | null>(null);
  const [notifError, setNotifError] = useState<string | null>(null);
  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setNotifLoading(true);
    setNotifResult(null);
    setNotifError(null);
    try {
      const res = await sendSingleNotification({
        title: notifTitle,
        body: notifBody,
        userId: user.user_id,
        token: user.fcm_token || undefined,
        data: { route: "profile" },
        dryRun: false,
      });
      setNotifResult(JSON.stringify(res, null, 2));
    } catch (err: any) {
      setNotifError(err.message);
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    // Önce state'den gelen veriyi kontrol et
    if (location.state && location.state.user) {
      setUser(location.state.user);
      setLoading(false);
    } else if (id) {
      // Eğer state'de veri yoksa API'den çek
      loadUser(id);
    }
  }, [id, location.state]);

  const loadUser = async (userId: string) => {
    try {
      setLoading(true);
      setError(null);

      // ✅ Admin endpoint mevcut: GET /users/{user_id}
      const userData = await fetchUser(userId);
      setUser(userData);
    } catch (err: any) {
      console.error("User fetch error:", err);
      setError(err.message || "Kullanıcı yüklenirken hata oluştu");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString("tr-TR");
    } catch {
      return "Bilinmiyor";
    }
  };

  const handleRefreshUser = () => {
    if (id) {
      loadUser(id);
    }
  };

  const getActivityStatus = (lastLogin: string) => {
    const date = new Date(lastLogin);
    const now = new Date();
    const diffDays = Math.ceil(
      Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays <= 7) return { color: "#10b981", label: "Aktif", icon: "🟢" };
    if (diffDays <= 30) return { color: "#f59e0b", label: "Pasif", icon: "🟡" };
    return { color: "#ef4444", label: "İnaktif", icon: "🔴" };
  };

  const handleCopyToClipboard = (text: string, identifier: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(identifier);
    setTimeout(() => setCopiedText(null), 2000);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <div className={styles.loadingSpinner}></div>
          <span>Kullanıcı yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className={styles.container}>
        <div className={styles.errorWrapper}>
          <h3>Hata</h3>
          <p>{error || "Kullanıcı bulunamadı"}</p>
          <button
            onClick={() => navigate("/users")}
            className={styles.errorButton}
          >
            Kullanıcı Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  const activityStatus = getActivityStatus(user.last_login);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <UserIcon className={styles.titleIcon} />
            {user.name || "İsimsiz"}
          </h1>
          <p className={styles.subtitle}>
            Kullanıcı detayları ve sistem bilgileri
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            onClick={() => navigate(`/users/${id}/edit`)}
            className={styles.primaryButton}
          >
            <Edit size={16} />
            Düzenle
          </button>
          <button
            onClick={() => navigate("/users")}
            className={styles.headerButton}
          >
            <ArrowLeft size={16} />
            Listeye Dön
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div
            className={styles.statIcon}
            style={{ background: activityStatus.color }}
          >
            <Activity size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{activityStatus.label}</h3>
            <p>Kullanıcı Durumu</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#3b82f6" }}>
            <Music size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{user.assigned_song_ids.length}</h3>
            <p>Atanmış Şarkı</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#8b5cf6" }}>
            <Smartphone size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>{user.devices.length}</h3>
            <p>Bağlı Cihaz</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: "#10b981" }}>
            <Calendar size={20} />
          </div>
          <div className={styles.statContent}>
            <h3>
              {Math.ceil(
                Math.abs(
                  new Date().getTime() - new Date(user.created_at).getTime()
                ) /
                  (1000 * 60 * 60 * 24)
              )}
            </h3>
            <p>Gün Önce Kayıt</p>
          </div>
        </div>
      </div>

      <div className={styles.contentGrid}>
        {/* Profile Information */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>
              <UserIcon size={18} />
              Profil Bilgileri
            </h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>
                  <UserIcon size={16} />
                  Ad Soyad
                </label>
                <span>{user.name || "İsimsiz"}</span>
              </div>

              <div className={styles.infoItem}>
                <label>
                  <Mail size={16} />
                  E-posta
                </label>
                <span>{user.email}</span>
              </div>

              <div className={styles.infoItem}>
                <label>
                  <Globe size={16} />
                  Dil
                </label>
                <span>{user.language || "english"}</span>
              </div>

              <div className={styles.infoItem}>
                <label>
                  <Trophy size={16} />
                  Puan
                </label>
                <span>{user.score ?? 0}</span>
              </div>

              <div className={styles.infoItem}>
                <label>Kullanıcı ID</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span className={styles.monospace}>{user.user_id}</span>
                  <button
                    onClick={() => handleCopyToClipboard(user.user_id, "user_id")}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: copiedText === "user_id" ? "#22C55E" : "var(--text-secondary)",
                      transition: "color 0.2s",
                      padding: "0.25rem",
                    }}
                    title="Kopyala"
                  >
                    {copiedText === "user_id" ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <div className={styles.infoItem}>
                <label>FCM Token</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "space-between" }}>
                  <span className={styles.monospace}>
                    {user.fcm_token
                      ? `${user.fcm_token.substring(0, 20)}...`
                      : "Yok"}
                  </span>
                  {user.fcm_token && (
                    <button
                      onClick={() => handleCopyToClipboard(user.fcm_token!, "fcm_token")}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: copiedText === "fcm_token" ? "#22C55E" : "var(--text-secondary)",
                        transition: "color 0.2s",
                        padding: "0.25rem",
                        flexShrink: 0,
                      }}
                      title="Kopyala"
                    >
                      {copiedText === "fcm_token" ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Information */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>
              <Activity size={18} />
              Aktivite Bilgileri
            </h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>
                  <Calendar size={16} />
                  Kayıt Tarihi
                </label>
                <span>{formatDate(user.created_at)}</span>
              </div>

              <div className={styles.infoItem}>
                <label>
                  <Activity size={16} />
                  Son Giriş
                </label>
                <span>{formatDate(user.last_login)}</span>
              </div>

              <div className={styles.infoItem}>
                <label>Durum</label>
                <span
                  className={styles.statusBadge}
                  style={{ background: activityStatus.color }}
                >
                  {activityStatus.icon} {activityStatus.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Songs */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>
              <Music size={18} />
              Atanmış Şarkılar ({user.assigned_song_ids.length})
            </h3>
            <button
              onClick={() => setShowSongModal(true)}
              className={styles.cardHeaderButton}
            >
              <Plus size={16} />
              Şarkı Ata
            </button>
          </div>
          <div className={styles.cardBody}>
            {user.assigned_song_ids.length > 0 ? (
              <div className={styles.tagGrid}>
                {user.assigned_song_ids.map((songId, index) => (
                  <span key={index} className={styles.tag}>
                    <Music size={12} />
                    {songId}
                  </span>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Music size={48} style={{ opacity: 0.3 }} />
                <p>Henüz atanmış şarkı yok</p>
                <button
                  onClick={() => setShowSongModal(true)}
                  className={styles.emptyStateButton}
                >
                  <Plus size={16} />
                  İlk Şarkıyı Ata
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Devices */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>
              <Smartphone size={18} />
              Bağlı Cihazlar ({user.devices.length})
            </h3>
            <button
              onClick={() => setShowDeviceModal(true)}
              className={styles.cardHeaderButton}
            >
              <Plus size={16} />
              Cihaz Ekle
            </button>
          </div>
          <div className={styles.cardBody}>
            {user.devices.length > 0 ? (
              <div className={styles.deviceList}>
                {user.devices.map((device, index) => (
                  <div key={index} className={styles.deviceCard}>
                    <div className={styles.deviceHeader}>
                      <div className={styles.deviceInfo}>
                        <h4>{device.model}</h4>
                        <span className={styles.deviceSerial}>
                          #{device.serial_number}
                        </span>
                      </div>
                      <div className={styles.deviceStatus}>
                        <span
                          className={styles.statusBadge}
                          style={{
                            background: device.is_active
                              ? "#10b981"
                              : "#ef4444",
                          }}
                        >
                          {device.is_active ? "🟢 Aktif" : "🔴 Pasif"}
                        </span>
                      </div>
                    </div>

                    <div className={styles.deviceDetails}>
                      <div className={styles.deviceMeta}>
                        <span>HW: {device.hardware_version || "N/A"}</span>
                        <span>FW: {device.firmware_version || "N/A"}</span>
                        <span>
                          Son Bağlantı: {formatDate(device.last_connected_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Smartphone size={48} style={{ opacity: 0.3 }} />
                <p>Henüz bağlı cihaz yok</p>
                <button
                  onClick={() => setShowDeviceModal(true)}
                  className={styles.emptyStateButton}
                >
                  <Plus size={16} />
                  İlk Cihazı Ekle
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>
              <Settings size={18} />
              Hızlı İşlemler
            </h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.actionGrid}>
              <button
                onClick={() => navigate(`/users/${id}/edit`)}
                className={styles.actionButton}
                style={{ background: "#3b82f6" }}
              >
                <Edit size={16} />
                Profili Düzenle
              </button>

              <button
                onClick={() => setShowSongModal(true)}
                className={styles.actionButton}
                style={{ background: "#8b5cf6" }}
              >
                <Music size={16} />
                Şarkı Ata
              </button>

              <button
                onClick={() => setShowDeviceModal(true)}
                className={styles.actionButton}
                style={{ background: "#f59e0b" }}
              >
                <Smartphone size={16} />
                Cihaz Ekle
              </button>

              <button
                onClick={handleRefreshUser}
                className={styles.actionButton}
                style={{ background: "#10b981" }}
              >
                <Activity size={16} />
                Yenile
              </button>

              <button
                onClick={() => setShowNotifModal(true)}
                className={styles.actionButton}
                style={{ background: "#f59e0b" }}
              >
                <Mail size={16} />
                Bildirim Gönder
              </button>
            </div>
          </div>
        </div>
            {/* Bildirim Gönder Modalı */}
            {showNotifModal && (
              <div style={{
                position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh", background: "#000a", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <Card className="card" style={{ minWidth: 340, maxWidth: 400 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Kişiye Bildirim Gönder</h2>
                  <form onSubmit={handleSendNotification} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Input
                      label="Başlık"
                      value={notifTitle}
                      onChange={e => setNotifTitle(e.target.value)}
                      placeholder="Bildirim başlığı"
                      required
                    />
                    <TextArea
                      label="Mesaj"
                      value={notifBody}
                      onChange={e => setNotifBody(e.target.value)}
                      placeholder="Bildirim mesajı"
                      required
                      rows={3}
                    />
                    <div style={{ fontSize: 12, color: "#f59e0b", marginBottom: 4 }}>
                      <b>Kullanıcı ID:</b> {user?.user_id}<br />
                      <b>FCM Token:</b> {user?.fcm_token ? user.fcm_token.substring(0, 20) + "..." : "Yok"}
                    </div>
                    <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                      <Button type="button" variant="ghost" onClick={() => setShowNotifModal(false)}>
                        Vazgeç
                      </Button>
                      <Button type="submit" variant="primary" disabled={notifLoading}>
                        {notifLoading ? "Gönderiliyor..." : "Gönder"}
                      </Button>
                    </div>
                    {notifError && <Badge variant="danger">{notifError}</Badge>}
                    {notifResult && (
                      <pre style={{
                        background: "#181f2a",
                        color: "#f1f5f9",
                        borderRadius: 12,
                        padding: 12,
                        fontSize: 13,
                        marginTop: 8,
                        overflow: "auto"
                      }}>{notifResult}</pre>
                    )}
                  </form>
                </Card>
              </div>
            )}
      </div>

      {/* Modals */}
      <AddDeviceModal
        isOpen={showDeviceModal}
        onClose={() => setShowDeviceModal(false)}
        onDeviceAdded={handleRefreshUser}
        userId={user.user_id}
      />

      <AssignSongModal
        isOpen={showSongModal}
        onClose={() => setShowSongModal(false)}
        onSongAssigned={handleRefreshUser}
        userId={user.user_id}
        assignedSongIds={user.assigned_song_ids}
      />
    </div>
  );
};

export default UserDetail;
